/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { type ReactElement, useCallback } from 'react';
import { Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/buttons';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/forms/form';
import { Input } from '@/components/forms/input';
import { Spinner } from '@/components/spinner';
import { useToast } from '@/components/toast';
import { useMarketingActivity } from '@/hooks/use-marketing-activity';
import type { ContentAssetBody } from '@/lib/api/content-asset';
import { cn } from '@/lib/utils';
import { fireCreatedLeadEvent } from '@/lib/adapters/engagement/wert';

interface NewsletterSignupProps {
    className?: string;
    /**
     * Content asset markup for the newsletter heading. `null` (or a missing/empty body) triggers
     * an i18n-based fallback so the form remains usable when the content asset is unpublished
     * or the SCAPI call fails. See loader in `src/routes/_app.tsx`.
     */
    headingContent: ContentAssetBody | null;
}

interface NewsletterSignupFormValues {
    email: string;
}

export default function NewsletterSignup({ className, headingContent }: NewsletterSignupProps): ReactElement {
    const { t } = useTranslation('footer');
    const { subscribe, isPending } = useMarketingActivity();
    const { addToast } = useToast();

    const form = useForm<NewsletterSignupFormValues>({
        defaultValues: {
            email: '',
        },
    });

    const handleSubmit = useCallback(
        async (values: NewsletterSignupFormValues) => {
            const email = values.email.trim();
            if (!email) return;

            // useMarketingActivity is contractually non-throwing (returns { ok: false, error }
            // on failure), but we defensively catch here so a hook regression cannot leave the
            // form in a stuck pending state or surface an uncaught rejection to the page.
            let result: Awaited<ReturnType<typeof subscribe>>;
            try {
                result = await subscribe({ email, source: 'footer' });
            } catch {
                result = { ok: false };
            }

            if (result.ok) {
                // Prefer the merchandiser-authored `description` on the heading content asset so the
                // success copy can be edited in Business Manager alongside the heading. Falls back to
                // the i18n string when the asset is unavailable or the description is empty.
                const confirmation = headingContent?.description?.trim() || t('newsletter.subscribeSuccess');
                addToast(confirmation, 'success');
                form.reset();

                // Fire WERT created_lead — emailSha256 hashed server-side, raw email never passed
                const emailSha256 = (result as { emailSha256?: string }).emailSha256;
                fireCreatedLeadEvent({ emailSha256, widget: 'footer_newsletter' });

                return;
            }

            form.setError('email', {
                type: 'server',
                message: t('newsletter.subscribeError'),
            });
        },
        [subscribe, addToast, form, t, headingContent]
    );

    const headingBody = headingContent?.body?.trim();
    const headingInnerClassName =
        '[&_a]:text-footer-foreground [&_a]:underline [&_a]:underline-offset-2 [&_a]:transition-colors [&_a:hover]:text-footer-foreground/80 [&_a:focus-visible]:text-footer-foreground/80';

    return (
        <section
            aria-labelledby="newsletter-heading"
            className={cn('bg-footer-background text-footer-foreground', className)}>
            <Form {...form}>
                <form
                    onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
                    className="flex w-full flex-col gap-4"
                    data-testid="newsletter-signup-form"
                    aria-busy={isPending}
                    noValidate>
                    <h2
                        id="newsletter-heading"
                        className="inline text-xs font-semibold uppercase leading-4 tracking-button">
                        <Mail aria-hidden="true" className="mr-1.5 inline size-4 align-[-0.125em]" />
                        {headingBody ? (
                            <span
                                className={headingInnerClassName}
                                // SFCC content assets are authored by trusted merchandisers in Business Manager.
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: headingBody }}
                            />
                        ) : (
                            <span className={headingInnerClassName}>
                                {t('newsletter.title')} <a href="#">{t('newsletter.learnMore')}</a>
                            </span>
                        )}
                    </h2>

                    <div className="flex w-full items-start gap-2">
                        <FormField
                            control={form.control}
                            name="email"
                            rules={{
                                required: t('newsletter.emailRequired'),
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: t('newsletter.emailInvalid'),
                                },
                            }}
                            render={({ field }) => (
                                <FormItem className="flex-1 gap-1">
                                    <FormControl>
                                        <Input
                                            {...field}
                                            type="email"
                                            required
                                            disabled={isPending}
                                            placeholder={t('newsletter.emailAddressPlaceholder')}
                                            aria-label={t('newsletter.emailLabel')}
                                            data-testid="newsletter-signup-input"
                                            className="h-12 border-footer-foreground/45 bg-input-30 text-footer-foreground placeholder:text-footer-foreground/80 shadow-none focus-visible:border-footer-foreground"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <Button
                            type="submit"
                            variant="outline"
                            size="default"
                            disabled={isPending}
                            aria-busy={isPending}
                            data-testid="newsletter-signup-submit"
                            className="h-12 shrink-0 border-footer-foreground bg-footer-foreground px-4 text-footer-background hover:bg-footer-foreground/90 hover:text-footer-background">
                            {isPending ? (
                                <>
                                    <Spinner
                                        size="sm"
                                        aria-hidden="true"
                                        // Spinner defaults to `border-border border-t-primary`, which is
                                        // tuned for light backgrounds. The footer signup button is a
                                        // light-on-dark contrast pill — override to keep the spinning
                                        // arc visible against the `bg-footer-foreground` button face.
                                        className="border-footer-background/30 border-t-footer-background"
                                    />
                                    <span className="sr-only">{t('newsletter.subscribingButton')}</span>
                                </>
                            ) : (
                                t('newsletter.signUpButton')
                            )}
                        </Button>
                    </div>
                </form>
            </Form>
        </section>
    );
}
